import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Col, Form, Row, Spinner } from 'react-bootstrap';
import SistemaToast from '../../components/SistemaToast/SistemaToast';
import PageHeader from '../../components/PageHeader/PageHeader';
import { useSistemaToast } from '../../components/SistemaToast/useSistemaToast';
import DataTable from 'react-data-table-component';
import { LuEye, LuPencilLine } from 'react-icons/lu';
import { formatDateTimeChile, urlApiProd } from '../utiles';
import CrearPackingListDialog from './CrearPackingListDialog';
import { INVENTARIO_TABLE_STYLES } from './inventarioGridUtils';
import {
  expandirPackingListsPorDraft,
  nombrePackingListEnListado,
} from './inventarioPackingListsUtils';

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function fmtFecha(iso) {
  return formatDateTimeChile(iso);
}

function mesAnioActual() {
  const n = new Date();
  return { mes: String(n.getMonth() + 1), anio: String(n.getFullYear()) };
}

function fechaCreacionEnMesAnio(iso, mes, anio) {
  if (!iso || !mes || !anio) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return d.getMonth() + 1 === parseInt(mes, 10) && d.getFullYear() === parseInt(anio, 10);
}

const MESES_FILTRO = [...Array(12).keys()].map((i) => ({
  value: String(i + 1),
  label: new Date(2000, i, 1).toLocaleString('es-CL', { month: 'long' }),
}));

const ANIOS_FILTRO = Array.from({ length: 6 }, (_, i) => String(new Date().getFullYear() - i));

export default function InventarioPackingLists() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtros, setFiltros] = useState(() => {
    const { mes, anio } = mesAnioActual();
    return { nombre: '', pi: '', mes, anio };
  });
  const [editPl, setEditPl] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const sistemaToast = useSistemaToast();
  const [notasDisponibles, setNotasDisponibles] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${urlApiProd}/inventario/packinglists`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('No se pudieron cargar los packing lists');
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'Error al cargar');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadNotas = useCallback(async () => {
    try {
      const res = await fetch(`${urlApiProd}/inventario/nfs`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setNotasDisponibles(Array.isArray(data) ? data : []);
    } catch {
      setNotasDisponibles([]);
    }
  }, []);

  useEffect(() => {
    load();
    loadNotas();
  }, [load, loadNotas]);

  const { mes: mesActual, anio: anioActual } = mesAnioActual();
  const hayFiltros =
    !!filtros.nombre.trim() ||
    !!filtros.pi.trim() ||
    filtros.mes !== mesActual ||
    filtros.anio !== anioActual;

  const limpiarFiltros = () => {
    const { mes, anio } = mesAnioActual();
    setFiltros({ nombre: '', pi: '', mes, anio });
  };

  const rowsVisuales = useMemo(() => expandirPackingListsPorDraft(rows), [rows]);

  const filtrados = useMemo(() => {
    const n = filtros.nombre.trim().toLowerCase();
    const p = filtros.pi.trim().toLowerCase();
    const { mes, anio } = filtros;
    return rowsVisuales.filter((r) => {
      if (
        n &&
        !nombrePackingListEnListado(r).toLowerCase().includes(n)
      ) return false;
      if (p && !String(r.pi_seleccionada || '').toLowerCase().includes(p)) return false;
      if (!fechaCreacionEnMesAnio(r.fecha_creacion, mes, anio)) return false;
      return true;
    });
  }, [rowsVisuales, filtros]);

  const columns = useMemo(
    () => [
      {
        name: 'Nombre de packing list',
        selector: (r) => nombrePackingListEnListado(r),
        sortable: true,
        grow: 2,
        cell: (r) => (
          <span>
            {nombrePackingListEnListado(r)}
            {r.estado === 'borrador' ? (
              <span className="badge bg-secondary ms-2">Borrador</span>
            ) : r.estado === 'revision' ? (
              <span className="badge bg-danger ms-2">Revisión</span>
            ) : r.estado === 'historico' ? (
              <span className="badge bg-warning text-dark ms-2">Histórico</span>
            ) : (
              <span className="badge bg-success ms-2">Emitido</span>
            )}
          </span>
        ),
      },
      {
        name: 'PI seleccionada',
        selector: (r) => r.pi_seleccionada,
        sortable: true,
        grow: 2,
      },
      {
        name: 'Fecha de creación',
        selector: (r) => r.fecha_creacion,
        sortable: true,
        cell: (r) => fmtFecha(r.fecha_creacion),
      },
      {
        name: '',
        width: '70px',
        ignoreRowClick: true,
        cell: (r) => {
          const historico = r.estado === 'historico';
          return (
            <button
              type="button"
              className={`btn btn-sm ${historico ? 'btn-outline-secondary' : 'btn-outline-primary'}`}
              title={historico ? 'Ver packing list histórico' : 'Editar'}
              onClick={() => {
                setEditPl(r);
                setShowDialog(true);
              }}
            >
              {historico ? <LuEye /> : <LuPencilLine />}
            </button>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="container-fluid ui-page">
      <SistemaToast
        show={sistemaToast.show}
        message={sistemaToast.message}
        onClose={sistemaToast.hide}
      />

      <CrearPackingListDialog
        show={showDialog}
        onHide={() => {
          setShowDialog(false);
          setEditPl(null);
        }}
        packingExistente={editPl}
        seleccionInicial={[]}
        notasDisponibles={notasDisponibles}
        onSaved={(data, meta = {}) => {
          const eraEdicion = !!editPl?.id;
          load();
          loadNotas();
          if (meta.redirecting || meta.emitirExcel) return;
          const texto = eraEdicion
            ? data?.nombre
              ? `Packing list "${data.nombre}" actualizado con éxito`
              : 'Packing list actualizado con éxito'
            : data?.nombre
              ? `Packing list "${data.nombre}" creado con éxito`
              : 'Packing list creado con éxito';
          sistemaToast.notify(texto);
        }}
      />

      <PageHeader eyebrow="Logística" title="Packing List" descripcion="Listado de packing lists creados desde las notas fiscales del inventario." />
      <div className="card">
        <div className="card-body mb-2">
          {loading ? (
            <>
              <div className="text-center">
                <Spinner animation="border" role="status" />
              </div>
              <div className="text-center">
                <span className="sr-only">Cargando...</span>
              </div>
            </>
          ) : (
            <>
              {error ? (
                <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-3">
                  {error}
                </Alert>
              ) : null}

              <div className="card mb-3">
                <div className="card-body py-3">
                  <p className="text-muted small mb-3">
                    Listado de packing lists creados desde notas fiscales del inventario. Use los
                    filtros para buscar por nombre, PI o mes de creación. Para crear uno nuevo,
                    selecciónelo desde Inventario.
                  </p>

                  <Row className="g-2 mb-0 align-items-end">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label htmlFor="pl-filtro-nombre" className="mb-1 small">
                          Nombre de packing list
                        </Form.Label>
                        <Form.Control
                          id="pl-filtro-nombre"
                          value={filtros.nombre}
                          onChange={(e) => setFiltros((f) => ({ ...f, nombre: e.target.value }))}
                          placeholder="Buscar nombre…"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label htmlFor="pl-filtro-pi" className="mb-1 small">
                          PI seleccionada
                        </Form.Label>
                        <Form.Control
                          id="pl-filtro-pi"
                          value={filtros.pi}
                          onChange={(e) => setFiltros((f) => ({ ...f, pi: e.target.value }))}
                          placeholder="Buscar PI…"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Label className="mb-1 small d-block">Mes / año de creación</Form.Label>
                      <Row className="g-2">
                        <Col xs={7}>
                          <Form.Select
                            id="pl-filtro-mes"
                            value={filtros.mes}
                            onChange={(e) => setFiltros((f) => ({ ...f, mes: e.target.value }))}
                            aria-label="Mes de creación"
                          >
                            {MESES_FILTRO.map((m) => (
                              <option key={m.value} value={m.value}>
                                {m.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col xs={5}>
                          <Form.Select
                            id="pl-filtro-anio"
                            value={filtros.anio}
                            onChange={(e) => setFiltros((f) => ({ ...f, anio: e.target.value }))}
                            aria-label="Año de creación"
                          >
                            {ANIOS_FILTRO.map((y) => (
                              <option key={y} value={y}>
                                {y}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </div>
              </div>

              <div className="card">
                <div className="card-header py-2 d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <h6 className="mb-0">
                    Packing lists
                    {rowsVisuales.length > 0 ? (
                      <span className="text-muted fw-normal small ms-2">
                        {filtrados.length} registro(s)
                        {hayFiltros ? ` de ${rowsVisuales.length}` : ''}
                      </span>
                    ) : null}
                  </h6>
                  {hayFiltros ? (
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={limpiarFiltros}
                    >
                      Limpiar filtros
                    </button>
                  ) : null}
                </div>
                <div className="card-body">
                  <DataTable
                    columns={columns}
                    data={filtrados}
                    keyField="_rowKey"
                    customStyles={INVENTARIO_TABLE_STYLES}
                    pagination
                    paginationPerPage={20}
                    highlightOnHover
                    dense
                    noHeader
                    responsive
                    noDataComponent={
                      <div className="p-4 text-muted text-center">
                        {rows.length > 0 && hayFiltros
                          ? 'Ningún registro coincide con los filtros'
                          : 'No hay packing lists de inventario'}
                      </div>
                    }
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
